import { type ReactNode } from "react";
import { FormField, FormContent, FormItem, FormLabel, FormControl, FormMessage } from "@core/components/ui/Form";
import { Input, type InputProps } from "@core/components/ui/Input";
import { cn } from "@core/utils";
import type { Control, FieldValues, FieldPath } from "react-hook-form";

/**
 * 폼 입력 컴포넌트의 속성을 정의하는 인터페이스
 * @template T - 폼 필드 값의 타입
 */
interface FormInputAddPropsType<T extends FieldValues = FieldValues> {
  /** FormInputProps: 폼 필드의 이름 */
  name: FieldPath<T>;
  /** FormInputProps: 입력 필드의 레이블 텍스트 */
  label?: string | ReactNode;
  /** FormInputProps: react-hook-form의 폼 컨트롤 객체 */
  control: Control<T>;
  /** FormInputProps: 모바일일 때 레이블 컬럼 배치 여부 */
  useMobileLabelColumn?: boolean;
  /** FormInputProps: 필수 입력 여부 */
  required?: boolean;
  /** FormInputProps: FormItem 컴포넌트에 적용할 추가 클래스 */
  itemClassName?: string;
  /** FormInputProps: FormLabel에 적용할 추가 클래스 */
  labelClassName?: string;
  /** FormInputProps: 입력 필드를 감싸는 div태그에 적용할 추가 클래스 */
  inputWrapClassName?: string;
  /** FormInputProps: 입력 필드에 적용할 추가 클래스 */
  inputClassName?: string;
  /** FormInputProps: 에러 메세지 끄기 */
  offMessage?: boolean;
  /** FormInputProps: 추가적인 자식 컴포넌트 */
  children?: ReactNode;

  type?: string;
}

export type FormInputPropsType<T extends FieldValues = FieldValues> = FormInputAddPropsType<T> & InputProps;

export function FormInput<T extends FieldValues = FieldValues>({ name, label, control, useMobileLabelColumn, offMessage, required, itemClassName, labelClassName, inputWrapClassName, inputClassName, children, type, ...inputProps }: FormInputPropsType<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("wrapper-form-item form-input", useMobileLabelColumn && "max-lg:block!", itemClassName)}>
          {label && (
            <FormLabel className={cn(labelClassName)} required={required}>
              {label}
            </FormLabel>
          )}
          <FormContent className={cn(inputWrapClassName)}>
            <FormControl>
              <Input
                {...inputProps}
                {...field}
                type={type}
                className={cn(inputClassName)}
                onChange={(e) => {
                  inputProps?.onChange?.(e); // 사용자 정의 onChange 실행
                  field?.onChange(e); // react-hook-form 상태 갱신
                }}
              />
            </FormControl>

            {!offMessage && <FormMessage />}
            {children}
          </FormContent>
        </FormItem>
      )}
    />
  );
}
